// v10.0 — Local-first DAW project persistence via IndexedDB.
//
// Purpose: mirror the entire Zustand DAW store snapshot to the browser's
// IndexedDB every ~8 seconds so that a page refresh, accidental tab
// close, network drop, or server timeout NEVER wipes a producer's
// unsaved work. On DAW mount, if a local snapshot exists AND is
// newer than the last cloud save (or the cloud save is unavailable),
// we hydrate from IndexedDB before the user has a chance to lose work.
//
// This is a SAFETY NET — it works alongside (not instead of) the cloud
// auto-save. Both fire in parallel; local is instant + offline-safe,
// cloud is authoritative + cross-device.
//
// Storage schema:
//   DB name : "crow_daw"
//   Store   : "sessions" keyed by "current" (single-row upsert)
//   Value   : { tracks, regions, bpm, projectName, projectId, effects,
//               masterVolume, savedAt (ISO), version (int) }
//
// We deliberately use the raw IndexedDB API (no `idb` dep) to keep the
// bundle lean — the surface area we need is tiny.

const DB_NAME = "crow_daw";
const STORE = "sessions";
const NAMED_STORE = "named_sessions"; // v16.1 — multi-session library
const KEY = "current";
const SCHEMA_VERSION = 2; // v16.1 — bumped for named_sessions store

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      // SSR / very old browser — swallow silently so callers no-op.
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, SCHEMA_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
      // v16.1 — named_sessions store: multiple manually-saved sessions
      // keyed by generated session id. Each row carries its own
      // metadata (name, savedAt) so the LoadSessionsModal can list
      // them by project name + last-modified date.
      if (!db.objectStoreNames.contains(NAMED_STORE)) {
        db.createObjectStore(NAMED_STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

// The list of Zustand store keys we actually want to persist. We
// intentionally SKIP transient UI state (activePlugin, selectedTrackId,
// etc.) so a restored session lands the producer back on a clean
// arranger view rather than mid-modal.
const PERSISTED_KEYS = [
  "tracks", "regions", "bpm", "projectName", "projectId",
  "effects", "masterVolume",
];

function snapshotFromState(state) {
  const snap = {};
  for (const k of PERSISTED_KEYS) {
    if (state[k] !== undefined) snap[k] = state[k];
  }
  snap.savedAt = new Date().toISOString();
  snap.version = SCHEMA_VERSION;
  return snap;
}

// Fire-and-forget upsert. Never throws — persistence must never block
// or crash the DAW.
export async function saveLocalSnapshot(state) {
  try {
    const db = await openDb();
    const snap = snapshotFromState(state);
    // Only persist snapshots with actual project content — avoids
    // clobbering a cloud-restored session with an empty default store.
    if (!snap.tracks || snap.tracks.length === 0) return;
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE).put(snap, KEY);
    });
  } catch (e) {
    // Silent — quota errors / private-mode restrictions shouldn't crash.
    // eslint-disable-next-line no-console
    console.warn("[localPersistence] save skipped:", e?.message || e);
  }
}

// Returns the last-saved snapshot or null. Never throws.
export async function loadLocalSnapshot() {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[localPersistence] load skipped:", e?.message || e);
    return null;
  }
}

// Clears the current row — call after a successful cloud save + fresh
// session so the next tab open doesn't re-hydrate stale state.
export async function clearLocalSnapshot() {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE).delete(KEY);
    });
  } catch { /* noop */ }
}

// Subscribe hook: call once on DAW mount. Sets up an 8s interval that
// snapshots the current store, and returns an unsubscribe fn to be
// invoked from the useEffect cleanup.
export function startLocalPersistenceLoop(useDAWStore, intervalMs = 8000) {
  let stopped = false;
  const tick = () => {
    if (stopped) return;
    try { saveLocalSnapshot(useDAWStore.getState()); } catch { /* noop */ }
  };
  const id = setInterval(tick, intervalMs);
  // Also snapshot immediately on tab-hide so producers who alt-tab away
  // never lose the very last edit.
  const onHide = () => { if (document.visibilityState === "hidden") tick(); };
  document.addEventListener("visibilitychange", onHide);
  // beforeunload — one more shot at persisting on refresh / close.
  const onBeforeUnload = () => tick();
  window.addEventListener("beforeunload", onBeforeUnload);
  return () => {
    stopped = true;
    clearInterval(id);
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("beforeunload", onBeforeUnload);
  };
}

// -------------------- v16.1: NAMED SESSION LIBRARY --------------------
//
// Manual saves land in an IndexedDB store keyed by a stable session id
// (auto-generated on first save, then reused so subsequent saves of the
// same session overwrite in place). Each row carries its own snapshot
// PLUS metadata (`name`, `savedAt`) so the Load Sessions modal can list
// entries by project name and last-modified date.

function _newId() {
  return `ses_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// Persist the current store as a named session. If `id` is supplied and
// exists, overwrites that row; otherwise creates a new row.
export async function saveNamedSession(state, { id = null, name = null } = {}) {
  const db = await openDb();
  const snap = snapshotFromState(state);
  const rowId = id || _newId();
  const row = {
    ...snap,
    id: rowId,
    name: (name || state.projectName || "Untitled Project").toString().slice(0, 120),
    savedAt: new Date().toISOString(),
  };
  await new Promise((resolve, reject) => {
    const tx = db.transaction(NAMED_STORE, "readwrite");
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
    tx.objectStore(NAMED_STORE).put(row);
  });
  return row;
}

// List every saved session as [{id, name, savedAt, bpm, trackCount}]
// sorted by savedAt descending. Never throws.
export async function listSavedSessions() {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(NAMED_STORE, "readonly");
      const req = tx.objectStore(NAMED_STORE).getAll();
      req.onsuccess = () => {
        const rows = (req.result || []).map((r) => ({
          id: r.id,
          name: r.name || r.projectName || "Untitled Project",
          savedAt: r.savedAt,
          bpm: r.bpm,
          trackCount: Array.isArray(r.tracks) ? r.tracks.length : 0,
        }));
        rows.sort((a, b) => (b.savedAt || "").localeCompare(a.savedAt || ""));
        resolve(rows);
      };
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[localPersistence] list skipped:", e?.message || e);
    return [];
  }
}

// Load a named session by id. Returns the full snapshot or null.
export async function loadNamedSession(id) {
  const db = await openDb();
  return await new Promise((resolve, reject) => {
    const tx = db.transaction(NAMED_STORE, "readonly");
    const req = tx.objectStore(NAMED_STORE).get(id);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function deleteNamedSession(id) {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(NAMED_STORE, "readwrite");
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
      tx.objectStore(NAMED_STORE).delete(id);
    });
  } catch { /* noop */ }
}
